import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    if (error.response?.status >= 500) {
      import('sonner').then(({ toast }) => {
        toast.error('Server error. Please try again.');
      });
    }
    if (!error.response && error.code !== 'ERR_CANCELED') {
      import('sonner').then(({ toast }) => {
        toast.error('Network error. Check your connection.');
      });
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => API.post('/auth/register', data),
  login: (data) => API.post('/auth/login', data),
  getMe: () => API.get('/auth/me'),
  updateMe: (data) => API.put('/auth/me', data),
  refresh: () => API.post('/auth/refresh'),
};

// Chatbots API
export const chatbotsAPI = {
  list: () => API.get('/chatbots'),
  get: (id) => API.get(`/chatbots/${id}`),
  create: (data) => API.post('/chatbots', data),
  update: (id, data) => API.put(`/chatbots/${id}`, data),
  delete: (id) => API.delete(`/chatbots/${id}`),
  getStats: (id) => API.get(`/chatbots/${id}/stats`),
  getConversations: (id, params) => API.get(`/chatbots/${id}/conversations`, { params }),
  getTemplates: () => API.get('/chatbots/templates'),
};

// Chat API (public)
export const chatAPI = {
  sendMessage: (chatbotId, data) => API.post(`/chat/${chatbotId}/message`, data),
  getConfig: (chatbotId) => API.get(`/chat/${chatbotId}/config`),
};

// Conversations API
export const conversationsAPI = {
  list: (params) => API.get('/conversations', { params }),
  get: (id) => API.get(`/conversations/${id}`),
  end: (id, rating) => API.post(`/conversations/${id}/end`, null, { params: { rating } }),
};

// Knowledge Bases API
export const knowledgeBasesAPI = {
  list: () => API.get('/knowledge-bases'),
  get: (id) => API.get(`/knowledge-bases/${id}`),
  create: (data) => API.post('/knowledge-bases', data),
  update: (id, data) => API.put(`/knowledge-bases/${id}`, data),
  delete: (id) => API.delete(`/knowledge-bases/${id}`),
  uploadDocument: (kbId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post(`/knowledge-bases/${kbId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addTextDocument: (kbId, data) => API.post(`/knowledge-bases/${kbId}/documents/text`, data),
  deleteDocument: (kbId, docId) => API.delete(`/knowledge-bases/${kbId}/documents/${docId}`),
  search: (kbId, query, topK = 5) => API.post(`/knowledge-bases/${kbId}/search`, null, { params: { query, top_k: topK } }),
};

// Analytics API
export const analyticsAPI = {
  getDashboard: () => API.get('/analytics/dashboard'),
  getChatbotAnalytics: (id, period = 'week') => API.get(`/analytics/chatbots/${id}`, { params: { period } }),
  getOverall: (period = 'week') => API.get('/analytics/overall', { params: { period } }),
  getUsage: () => API.get('/analytics/usage'),
  // Conversation Intelligence APIs
  getIntelligenceOverview: (period = 'week', chatbotId = null) => 
    API.get('/analytics/intelligence/overview', { params: { period, chatbot_id: chatbotId } }),
  getFunnel: (period = 'week', chatbotId = null) => 
    API.get('/analytics/intelligence/funnel', { params: { period, chatbot_id: chatbotId } }),
  getSuccessRate: (period = 'week', chatbotId = null) => 
    API.get('/analytics/intelligence/success-rate', { params: { period, chatbot_id: chatbotId } }),
  getUnanswered: (period = 'week', chatbotId = null, limit = 20) => 
    API.get('/analytics/intelligence/unanswered', { params: { period, chatbot_id: chatbotId, limit } }),
  getHeatmap: (period = 'week', chatbotId = null) => 
    API.get('/analytics/intelligence/heatmap', { params: { period, chatbot_id: chatbotId } }),
  setConversationOutcome: (conversationId, outcome, conversionValue = 0) =>
    API.post(`/analytics/intelligence/conversations/${conversationId}/outcome`, null, { 
      params: { outcome, conversion_value: conversionValue } 
    }),
  triggerBackfill: (chatbotId = null) =>
    API.post('/analytics/intelligence/backfill', null, { params: { chatbot_id: chatbotId } }),
  // Phase 2 - Business Intelligence APIs
  getHighIntent: (period = 'week', chatbotId = null, limit = 20) =>
    API.get('/analytics/intelligence/high-intent', { params: { period, chatbot_id: chatbotId, limit } }),
  getRevenue: (period = 'week', chatbotId = null) =>
    API.get('/analytics/intelligence/revenue', { params: { period, chatbot_id: chatbotId } }),
  addRevenueAttribution: (data) =>
    API.post('/analytics/intelligence/revenue/attribute', data),
  getKnowledgeCoverage: (period = 'week', chatbotId = null) =>
    API.get('/analytics/intelligence/knowledge-coverage', { params: { period, chatbot_id: chatbotId } }),
  getChatbotComparison: (period = 'week') =>
    API.get('/analytics/intelligence/chatbot-comparison', { params: { period } }),
  // AI Insights APIs (Feature 1, 2, 3)
  getAIInsights: (period = 'week', chatbotId = null) =>
    API.get('/ai-insights/insights', { params: { period, chatbot_id: chatbotId } }),
  getWeeklyReport: (chatbotId = null) =>
    API.get('/ai-insights/weekly-report', { params: { chatbot_id: chatbotId } }),
  getOptimizationSuggestions: (period = 'week', chatbotId = null) =>
    API.get('/ai-insights/optimization', { params: { period, chatbot_id: chatbotId } }),
  applySuggestion: (suggestionId) =>
    API.post(`/ai-insights/optimization/${suggestionId}/apply`),
  rejectSuggestion: (suggestionId) =>
    API.post(`/ai-insights/optimization/${suggestionId}/reject`),
  getConversationsList: (page = 1, pageSize = 20, chatbotId = null, filterType = null) =>
    API.get('/ai-insights/conversations', { params: { page, page_size: pageSize, chatbot_id: chatbotId, filter_type: filterType } }),
  getConversationReplay: (conversationId) =>
    API.get(`/ai-insights/conversations/${conversationId}/replay`),
};

// Billing API
export const billingAPI = {
  getPlans: () => API.get('/billing/plans'),
  createCheckout: (data) => API.post('/subscription/checkout', data),
  getCheckoutStatus: (sessionId) => API.get(`/subscription/checkout/${sessionId}/status`),
  getSubscription: () => API.get('/billing/subscription'),
  getHistory: () => API.get('/billing/history'),
};

// Subscription API
export const subscriptionAPI = {
  getStatus: () => API.get('/subscription/status'),
  getUsage: () => API.get('/subscription/usage'),
  getPlans: () => API.get('/subscription/plans'),
};

// Third-Party Integrations API (Shopify, Salesforce, Zendesk, Slack, HubSpot, Calendly, Google Calendar)
export const thirdPartyIntegrationsAPI = {
  getTypes: () => API.get('/third-party-integrations/types'),
  getActions: (type) => API.get(`/third-party-integrations/types/${type}/actions`),
  list: (chatbotId) => API.get(`/third-party-integrations${chatbotId ? `?chatbot_id=${chatbotId}` : ''}`),
  get: (id) => API.get(`/third-party-integrations/${id}`),
  create: (data) => API.post('/third-party-integrations', data),
  update: (id, data) => API.put(`/third-party-integrations/${id}`, data),
  delete: (id) => API.delete(`/third-party-integrations/${id}`),
  test: (id) => API.post(`/third-party-integrations/${id}/test`),
  execute: (id, action, parameters) => API.post(`/third-party-integrations/${id}/execute`, { action, parameters }),
};

// OAuth API for integrations
export const oauthAPI = {
  getConfig: () => API.get('/oauth/config'),
  startAuthorize: (integrationType, chatbotId) => API.get(`/oauth/${integrationType}/authorize?chatbot_id=${chatbotId}`),
  refreshToken: (integrationType, integrationId) => API.post(`/oauth/${integrationType}/refresh?integration_id=${integrationId}`),
};

// Integrations API (legacy - kept for compatibility)
export const integrationsAPI = {
  getChannels: () => API.get('/channels/available'),
  list: (chatbotId) => API.get('/channels/integrations', { params: chatbotId ? { chatbot_id: chatbotId } : {} }),
  create: (data) => API.post('/channels/integrations', data),
  update: (id, data) => API.put(`/channels/integrations/${id}`, data),
  delete: (id) => API.delete(`/channels/integrations/${id}`),
  // API Keys
  listAPIKeys: () => API.get('/integrations/api-keys'),
  createAPIKey: (data) => API.post('/integrations/api-keys', data),
  deleteAPIKey: (id) => API.delete(`/integrations/api-keys/${id}`),
};

// Channels API
export const channelsAPI = {
  getAvailable: () => API.get('/channels/available'),
  listIntegrations: (chatbotId) => API.get('/channels/integrations', { params: chatbotId ? { chatbot_id: chatbotId } : {} }),
  getIntegration: (id) => API.get(`/channels/integrations/${id}`),
  createIntegration: (data) => API.post('/channels/integrations', data),
  updateIntegration: (id, data) => API.put(`/channels/integrations/${id}`, data),
  deleteIntegration: (id) => API.delete(`/channels/integrations/${id}`),
  testIntegration: (id) => API.post(`/channels/integrations/${id}/test`),
  getMessages: (id, limit) => API.get(`/channels/integrations/${id}/messages`, { params: { limit } }),
};

// Settings API
export const settingsAPI = {
  getLLMProviders: () => API.get('/settings/llm-providers'),
  getVectorDBProviders: () => API.get('/settings/vector-db-providers'),
};

// Flows API
export const flowsAPI = {
  list: (chatbotId) => API.get('/flows', { params: { chatbot_id: chatbotId } }),
  get: (id) => API.get(`/flows/${id}`),
  create: (data) => API.post('/flows', data),
  update: (id, data) => API.put(`/flows/${id}`, data),
  delete: (id) => API.delete(`/flows/${id}`),
  publish: (id) => API.post(`/flows/${id}/publish`),
  duplicate: (id) => API.post(`/flows/${id}/duplicate`),
  getExecutions: (id, params) => API.get(`/flows/${id}/executions`, { params }),
  execute: (id, conversationId, variables) => 
    API.post(`/flows/${id}/execute`, null, { 
      params: { conversation_id: conversationId },
      ...(variables && { data: variables })
    }),
  executeStep: (executionId, userInput) => 
    API.post(`/flows/executions/${executionId}/step`, null, { 
      params: { user_input: userInput } 
    }),
  getExecution: (executionId) => API.get(`/flows/executions/${executionId}`),
  getNodeTemplates: () => API.get('/flows/templates/nodes'),
};

// Templates API
export const templatesAPI = {
  list: (category) => API.get('/templates', { params: category ? { category } : {} }),
  getCategories: () => API.get('/templates/categories'),
  get: (id) => API.get(`/templates/${id}`),
  use: (id) => API.post(`/templates/${id}/use`),
  useWithCustomization: (id, customization) => API.post(`/templates/${id}/use`, customization),
};

// Admin API (uses admin token)
const AdminAPI = axios.create({
  baseURL: `${BACKEND_URL}/api/admin`,
  headers: { 'Content-Type': 'application/json' },
});

AdminAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

AdminAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/mgmt-a8f4c2d9e1b7/login';
    }
    return Promise.reject(error);
  }
);

export const adminAPI = AdminAPI;

// Admin-auth axios instance with /api base (for routers not under /api/admin)
const AdminApiBase = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});
AdminApiBase.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
AdminApiBase.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      window.location.href = '/mgmt-a8f4c2d9e1b7/login';
    }
    return Promise.reject(error);
  }
);
export const adminAPIBase = AdminApiBase;

// Default export for convenience
export default API;
